// Procedural audio entry point: SFX (recipes in ./sfx), loops (./loops), and a
// small lookahead music sequencer. Everything no-ops until unlock() succeeds.

import type { AudioApi, LoopHandle } from '../core/contracts';
import { bus } from '../core/events';
import { save } from '../core/save';
import type { LoopId, MusicId, Settings, SfxId } from '../core/types';
import { LOOPS } from './loops';
import { createMixGraph, sliderGain, type MixGraph } from './mix';
import { SFX } from './sfx';
import { Voice, mtof } from './synth';

const NOOP_LOOP: LoopHandle = { stop() {}, setVolume() {}, setPitch() {} };

let ctx: AudioContext | null = null;
let mix: MixGraph | null = null;
const lastPlay = new Map<SfxId, number>();
const active = new Map<SfxId, Voice[]>();
let voiceCount = 0;
const MAX_VOICES = 40;

function ensure(): boolean {
  if (ctx) return true;
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return false;
    ctx = new AC({ latencyHint: 'interactive' });
    mix = createMixGraph(ctx);
    applyVolumes(save.settings);
    return true;
  } catch {
    ctx = null;
    mix = null;
    return false;
  }
}

function ready(): boolean {
  return !!ctx && !!mix && ctx.state === 'running';
}

function applyVolumes(s: Settings): void {
  if (!mix || !ctx) return;
  const t = ctx.currentTime;
  mix.master.gain.setTargetAtTime(sliderGain(s.masterVolume), t, 0.03);
  mix.music.gain.setTargetAtTime(sliderGain(s.musicVolume), t, 0.03);
  mix.sfx.gain.setTargetAtTime(sliderGain(s.sfxVolume), t, 0.03);
}

/** Per-voice output stage: dry → sfxIn, optional reverb send. */
function voiceOut(volume: number, reverb: number): GainNode {
  const g = ctx!.createGain();
  g.gain.value = volume;
  g.connect(mix!.sfxIn);
  if (reverb > 0) {
    const s = ctx!.createGain();
    s.gain.value = reverb;
    g.connect(s);
    s.connect(mix!.sfxVerb);
  }
  return g;
}

// ───────────────────────────── Music ─────────────────────────────

interface Track {
  bpm: number;
  root: number; // midi
  /** Chord roots (semitones from root) per bar, minor triads unless listed in major. */
  prog: number[];
  major?: number[];
  drums: 'none' | 'soft' | 'drive' | 'industrial';
  arp: boolean;
  bassType: OscillatorType;
  swing: number;
  level: number;
}

const TRACKS: Record<MusicId, Track> = {
  menu: { bpm: 92, root: 45, prog: [0, -4, 3, -2], major: [-4, 3], drums: 'soft', arp: true, bassType: 'sawtooth', swing: 0.04, level: 0.8 },
  hangar: { bpm: 84, root: 43, prog: [0, 5, -2, 3], major: [-2], drums: 'none', arp: true, bassType: 'triangle', swing: 0, level: 0.7 },
  graveyard: { bpm: 110, root: 40, prog: [0, -4, -7, -2], major: [-4, -2], drums: 'drive', arp: true, bassType: 'sawtooth', swing: 0.06, level: 0.85 },
  foundry: { bpm: 120, root: 38, prog: [0, 1, 0, -2], drums: 'industrial', arp: false, bassType: 'square', swing: 0, level: 0.85 },
  vault: { bpm: 96, root: 42, prog: [0, 6, 3, 1], drums: 'soft', arp: true, bassType: 'sine', swing: 0, level: 0.8 },
  boss: { bpm: 140, root: 40, prog: [0, 0, -4, -2], major: [-4], drums: 'drive', arp: true, bassType: 'sawtooth', swing: 0, level: 0.95 },
  results: { bpm: 76, root: 45, prog: [0, -4, 5, -2], major: [-4, 5, -2], drums: 'none', arp: false, bassType: 'triangle', swing: 0, level: 0.7 },
};

let musicId: MusicId | null = null;
let musicBus: GainNode | null = null;
let step = 0;
let nextStepAt = 0;
let timer: number | null = null;
let intensity = 0;

function note(midi: number, at: number, dur: number, peak: number, type: OscillatorType, cutoff: number, to: AudioNode): void {
  const v = new Voice(ctx!, to, at);
  v.tone({ type, f: mtof(midi), a: 0.01, h: dur * 0.4, d: dur * 0.9, peak, filter: { type: 'lowpass', f: cutoff, q: 2 } });
  v.finish();
}

function drum(kind: 'kick' | 'snare' | 'hat', at: number, peak: number, to: AudioNode): void {
  const v = new Voice(ctx!, to, at);
  if (kind === 'kick') v.tone({ type: 'sine', f: 140, f1: 42, fdur: 0.12, a: 0.002, d: 0.32, peak: peak * 1.2 });
  else if (kind === 'snare') {
    v.noise({ color: 'white', a: 0.002, d: 0.16, peak: peak * 0.5, filter: { type: 'bandpass', f: 1800, q: 0.8 } });
    v.tone({ type: 'triangle', f: 210, f1: 160, fdur: 0.08, a: 0.002, d: 0.1, peak: peak * 0.4 });
  } else v.noise({ color: 'white', a: 0.001, d: 0.04, peak: peak * 0.22, filter: { type: 'highpass', f: 8000 } });
  v.finish();
}

function scheduleStep(tr: Track, s: number, at: number, to: AudioNode): void {
  const bar = Math.floor(s / 16) % tr.prog.length;
  const pos = s % 16;
  const chordRoot = tr.root + tr.prog[bar];
  const third = tr.major?.includes(tr.prog[bar]) ? 4 : 3;
  const beat = 60 / tr.bpm;
  const sixteenth = beat / 4;
  const busy = intensity;

  // Pad: chord at the start of each bar.
  if (pos === 0) for (const iv of [12, 12 + third, 19]) note(chordRoot + iv, at, beat * 4, 0.05 * tr.level, 'sawtooth', 900 + busy * 900, to);
  // Bass: eighths, accent on beats.
  if (pos % 2 === 0) {
    const octave = pos % 8 === 6 ? 12 : 0;
    note(chordRoot + octave, at, sixteenth * 1.8, (pos % 4 === 0 ? 0.16 : 0.1) * tr.level, tr.bassType, 380 + busy * 600, to);
  }
  // Arp.
  if (tr.arp && (busy > 0.15 || tr.drums === 'none' || tr.drums === 'soft')) {
    const pattern = [0, third, 7, 12, 7, third, 12, 19];
    const n = chordRoot + 24 + pattern[pos % 8];
    note(n, at, sixteenth * 0.9, 0.045 * tr.level * (0.6 + busy * 0.6), 'square', 2200 + busy * 2000, to);
  }
  // Drums.
  if (tr.drums !== 'none') {
    const soft = tr.drums === 'soft';
    if (!soft || busy > 0.2) {
      if (pos % 4 === 0 && (tr.drums !== 'soft' || pos % 8 === 0)) drum('kick', at, 0.5 * tr.level, to);
      if (pos === 4 || pos === 12) {
        if (busy > 0.35 || tr.drums === 'industrial') drum('snare', at, 0.45 * tr.level, to);
      }
      if (busy > 0.5 && pos % 2 === 1) drum('hat', at, 0.4 * tr.level, to);
      if (tr.drums === 'industrial' && pos % 4 === 3) drum('hat', at, 0.6 * tr.level, to);
    }
  }
}

function tick(): void {
  if (!ready() || !musicId || !musicBus) return;
  const tr = TRACKS[musicId];
  const sixteenth = 60 / tr.bpm / 4;
  const horizon = ctx!.currentTime + 0.15;
  if (nextStepAt < ctx!.currentTime) nextStepAt = ctx!.currentTime + 0.05;
  while (nextStepAt < horizon) {
    const swing = step % 2 === 1 ? tr.swing * sixteenth * 4 : 0;
    scheduleStep(tr, step, nextStepAt + swing, musicBus);
    step++;
    nextStepAt += sixteenth;
  }
}

function startMusic(id: MusicId, fadeMs: number): void {
  if (!ctx || !mix) return;
  const t = ctx.currentTime;
  if (musicBus) {
    const old = musicBus;
    old.gain.setTargetAtTime(0, t, Math.max(0.05, fadeMs / 4000));
    setTimeout(() => old.disconnect(), fadeMs + 600);
  }
  musicBus = ctx.createGain();
  musicBus.gain.value = 0;
  musicBus.gain.setTargetAtTime(1, t, Math.max(0.05, fadeMs / 4000));
  musicBus.connect(mix.musicIn);
  const verb = ctx.createGain();
  verb.gain.value = 0.35;
  musicBus.connect(verb);
  verb.connect(mix.musicVerb);
  step = 0;
  nextStepAt = t + 0.1;
  if (timer === null) timer = window.setInterval(tick, 25);
}

// ───────────────────────────── API ─────────────────────────────

export const audio: AudioApi = {
  unlock() {
    if (!ensure()) return;
    if (ctx!.state === 'suspended') {
      void ctx!.resume().then(() => {
        if (musicId && !musicBus) startMusic(musicId, 600);
      });
    } else if (musicId && !musicBus) startMusic(musicId, 600);
  },

  play(id: SfxId, opts = {}) {
    if (!ready()) return;
    const def = SFX[id];
    if (!def) return;
    const now = ctx!.currentTime;
    const last = lastPlay.get(id) ?? -1;
    const gap = (def.gap ?? 25) / 1000;
    if (now - last < gap) return;
    if (voiceCount >= MAX_VOICES) return;
    lastPlay.set(id, now);
    const list = active.get(id) ?? [];
    if (def.max && list.length >= def.max) list.shift()?.kill(now + 0.02);
    const jitter = def.jitter ?? 0.04;
    const pitch = (opts.pitch ?? 1) * (1 + (Math.random() * 2 - 1) * jitter);
    const out = voiceOut(def.gain * (opts.volume ?? 1), def.reverb ?? 0.12);
    const v = new Voice(ctx!, out, now + 0.005, pitch);
    voiceCount++;
    v.onDone = () => {
      voiceCount--;
      out.disconnect();
      const l = active.get(id);
      if (l) {
        const i = l.indexOf(v);
        if (i >= 0) l.splice(i, 1);
      }
    };
    try {
      def.build(v);
      v.finish();
    } catch (err) {
      console.warn(`[audio] sfx ${id} failed`, err);
      v.dispose();
      return;
    }
    list.push(v);
    active.set(id, list);
    if (def.duck) mix!.duck(now, def.duck);
  },

  loop(id: LoopId, opts = {}) {
    if (!ready()) return NOOP_LOOP;
    const def = LOOPS[id];
    if (!def) return NOOP_LOOP;
    const out = voiceOut(def.gain * (opts.volume ?? 1), def.reverb ?? 0);
    out.gain.value = 0;
    out.gain.setTargetAtTime(def.gain * (opts.volume ?? 1), ctx!.currentTime, 0.05);
    const v = new Voice(ctx!, out, ctx!.currentTime + 0.01, opts.pitch ?? 1);
    try {
      def.build(v);
    } catch (err) {
      console.warn(`[audio] loop ${id} failed`, err);
      out.disconnect();
      return NOOP_LOOP;
    }
    let stopped = false;
    return {
      stop(fadeMs = 200) {
        if (stopped || !ctx) return;
        stopped = true;
        const t = ctx.currentTime;
        out.gain.setTargetAtTime(0, t, Math.max(0.01, fadeMs / 4000));
        v.kill(t + fadeMs / 1000 + 0.1);
        v.onDone = () => out.disconnect();
      },
      setVolume(vol: number) {
        if (!stopped && ctx) out.gain.setTargetAtTime(def.gain * vol, ctx.currentTime, 0.05);
      },
      setPitch(p: number) {
        if (stopped || !ctx) return;
        for (const pp of v.pitchParams) pp.param.setTargetAtTime(pp.base * p, ctx.currentTime, 0.05);
      },
    };
  },

  music(track: MusicId, opts = {}) {
    if (musicId === track && musicBus) return;
    musicId = track;
    if (ready()) startMusic(track, opts.fadeMs ?? 900);
  },

  stopMusic(fadeMs = 800) {
    musicId = null;
    if (musicBus && ctx) {
      const old = musicBus;
      old.gain.setTargetAtTime(0, ctx.currentTime, Math.max(0.05, fadeMs / 4000));
      setTimeout(() => old.disconnect(), fadeMs + 600);
    }
    musicBus = null;
  },

  setIntensity(v: number) {
    intensity = Math.max(0, Math.min(1, v));
  },

  applySettings(s: Settings) {
    applyVolumes(s);
  },
};

bus.on('settings:changed', () => applyVolumes(save.settings));
